import axios from 'axios';
import { Base64 } from 'js-base64';
import { Style } from 'mapbox-gl';
import { createStandardAction } from 'typesafe-actions';

import Agreement from '../models/Agreement';
import { LayerBounds } from '../models/Layer';
import { SearchField } from '../models/SearchField';
import { isAgreementLayer } from '../utils/layerMatch';
import { State } from './reducer';
import {
  getAgreementByFileNumber,
  getAgreementsPopupTab,
  getBasemapStyleByName,
  getDocumentsRequested,
  getLaserficheCredentials,
  getMapLayerBounds,
  getSelectedAgreement,
  getSelectedIdentifyFeature,
  getTodoActions
} from './selectors';

export const setTitle = createStandardAction('app/SET_TITLE')<string>();

export const setLaserficheVerification = createStandardAction('app/SET_LASERFICHE_VERIFICATION')<
  boolean
>();

export const signOutUser = createStandardAction('app/SIGN_OUT_USER')();

export const receiveAgreements = createStandardAction('app/RECEIVE_AGREEMENTS')<
  Record<string, Agreement>
>();

export const receiveDocuments = createStandardAction('app/RECEIVE_DOCUMENTS')<any>();

// Identify

export const setPopupLoading = createStandardAction('app/SET_POPUP_LOADING')<boolean>();

export const setIdentifyState = createStandardAction('app/SET_IDENTIFY_STATE')<{
  location: { lng: number; lat: number };
  features: Array<{ id: string; [key: string]: any }>;
}>();

export const clearIdentifyState = createStandardAction('app/CLEAR_IDENTIFY_STATE')();

export const setIdentifySelectedId = createStandardAction('app/SET_IDENTIFY_SELECTED_ID')<
  string | null
>();

/** Update identify state with a new location and features from a map click */
export const newIdentify = ({
  location,
  features
}: {
  location: { lng: number; lat: number };
  features: Array<{ id: string; [key: string]: any }>;
}) => (dispatch, getState: () => State) => {
  dispatch(setPopupLoading(true));
  dispatch(clearSelectedTableAgreement());
  if (getAgreementsPopupTab(getState())) {
    // Reset agreements tab to 0 if not set on new identify
    dispatch(setAgreementsPopupTab(0));
  }
  dispatch(setIdentifyState({ location, features }));
  dispatch(selectIdentifyFeature(features.length === 1 ? features[0].id : null));
  dispatch(setPopupLoading(false));
};

/** Completely clear any previous identifies */
export const clearIdentify = () => dispatch => {
  dispatch(clearIdentifyState());
  dispatch(clearSelectedTableAgreement());
  dispatch(setSelectedAgreementId(null));
};

/**
 * Select a single feature from the current identify entities to focus the identify map popup
 * @param payload id of the feature to select, probably like 'Layer:FeatureID'
 */
export const selectIdentifyFeature = (featureId: string | null) => (
  dispatch,
  getState: () => State
) => {
  dispatch(setIdentifySelectedId(featureId));

  const selectedFeature = getSelectedIdentifyFeature(getState());
  if (selectedFeature && isAgreementLayer(selectedFeature.layer)) {
    const agreement = getAgreementByFileNumber(selectedFeature.properties['File Number'])(
      getState()
    );
    dispatch(setSelectedAgreementId(agreement ? agreement.id : null));
  } else {
    dispatch(setSelectedAgreementId(null));
  }
};

/**
 * Clear previous identify selected feature.
 * Does not change the identify entities, just clears currently selected.
 */
export const clearSelectedIdentifyFeature = () => dispatch => {
  dispatch(setIdentifySelectedId(null));
  dispatch(setSelectedAgreementId(null));
};

/**
 * Set active tab (fields - 0 or files - 1) in the agreements popup component.
 * Active tab needs to be persisted in the store to support popup removes and adds.
 * @param payload index of the tab to set
 */
export const setAgreementsPopupTab = createStandardAction('app/SET_AGREEMENTS_POPUP_TAB')<number>();

export const setSidebarTab = createStandardAction('app/SET_SIDEBAR_TAB')<number>();

export const clearSearchFields = createStandardAction('app/CLEAR_SEARCH_FIELDS')();

export const removeSearchField = createStandardAction('app/REMOVE_SEARCH_FIELD')<string>();

export const addSearchField = createStandardAction('app/ADD_SEARCH_FIELD')<SearchField>();

export const replaceSearchField = createStandardAction('app/REPLACE_SEARCH_FIELD')<SearchField>();

export const modifySearchField = createStandardAction('app/MODIFY_SEARCH_FIELD')<
  Partial<SearchField>
>();

export const setToggleableLayer = createStandardAction('app/SET_TOGGLEABLE_LAYER')<string>();

export const setAgreementsTableOrder = createStandardAction('app/SET_AGREEMENTS_ORDER')<
  'asc' | 'desc',
  string
>();

export const setSelectedAgreementId = createStandardAction('app/SET_SELECTED_AGREEMENT_ID')<
  string | null
>();

export const setMapLayerBounds = createStandardAction('app/SET_MAP_LAYER_BOUNDS')<LayerBounds>();

export const setMapStyleBase = createStandardAction('app/SET_MAP_STYLE_BASE')<Style>();

export const setMapStyleBaseByName = (name: string) => (dispatch: any, getState: () => State) => {
  const style = getBasemapStyleByName(name)(getState());
  style && dispatch(setMapStyleBase(style));
};

export const setSelectedTableAgreement = createStandardAction('app/SET_SELECTED_TABLE_AGREEMENT')<
  any
>();

export const clearSelectedTableAgreement = createStandardAction(
  'app/CLEAR_SELECTED_TABLE_AGREEMENT'
)();

export const toggleAgreementsTableOpen = createStandardAction('app/TOGGLE_AGREEMENTS_TABLE_OPEN')();

export const newSelect = (agreementId: string | null) => (dispatch: any, getState: () => State) => {
  dispatch(setPopupLoading(true));
  dispatch(clearIdentifyState());
  dispatch(setAgreementsPopupTab(0));
  dispatch(setSelectedAgreementId(agreementId));
  const agreement = getSelectedAgreement(getState());
  const mapLayerBounds = getMapLayerBounds(getState());

  const selectedAgreementLocation = (agreement && mapLayerBounds[agreement.fileNumber]) || null;
  const bounds = selectedAgreementLocation && selectedAgreementLocation.bounds;
  const popupAnchor = selectedAgreementLocation && selectedAgreementLocation.popupAnchor;
  dispatch(setSelectedTableAgreement({ bounds, popupAnchor }));
  dispatch(setPopupLoading(false));
};

export const setLoading = createStandardAction('app/SET_LOADING')<boolean>();

export const setSignInDialogStatus = createStandardAction('app/SET_SIGN_IN_DIALOG_STATUS')<
  boolean
>();

export const setSignInDialogMessage = createStandardAction('app/SET_SIGN_IN_DIALOG_MESSAGE')<
  string
>();
export const setLaserficheUsernamePassword = createStandardAction(
  'app/SET_LASERFICHE_USERNAME_PASSWORD'
)<{
  username: string;
  password: string;
}>();

export const completeTodoAction = createStandardAction('app/COMPLETE_TODO_ACTION')();

export const addDocumentToTodoActions = createStandardAction('app/ADD_DOCUMENT_TO_TODO_ACTIONS')<
  string
>();

export const errorFetchingDocuments = createStandardAction('app/ERROR_FETCHING_DOCUMENTS')<{
  agreementId: string;
  errorMessage: string;
}>();

export const getSavedUser = () => (dispatch: any) => {
  const savedUser = false; // TODO check localStorage for saved user
  if (!savedUser) {
    dispatch(setSignInDialogStatus(true));
    return;
  }
  dispatch(runAdditionalTodoActions());
};

export const signInUser = (username: string, password: string) => (dispatch: any) => {
  dispatch(setLaserficheUsernamePassword({ username, password }));
  dispatch(runAdditionalTodoActions());
};

export const setDocumentsLoading = createStandardAction('app/SET_DOCUMENTS_LOADING')<{
  isLoading: boolean;
  agreementId: string;
}>();

export const requestDocuments = (id: string) => (dispatch: any) => {
  dispatch(addDocumentToTodoActions(id));
  dispatch(runAdditionalTodoActions());
};

export const runAdditionalTodoActions = () => async (dispatch: any, getState: () => State) => {
  const todoActions = getTodoActions(getState());
  if (!todoActions.length) {
    return;
  }

  const laserficheCredentials = getLaserficheCredentials(getState());
  const base64credentials = Base64.encode(
    `${laserficheCredentials.username}:${laserficheCredentials.password}`
  );

  switch (todoActions[0]) {
    case 'fetchAgreements':
      const AGREEMENTS_API = process.env.REACT_APP_AGREEMENTS_API || '';
      try {
        const response = await axios.get(AGREEMENTS_API, {
          headers: {
            Authorization: `Basic ${base64credentials}`
          }
        });
        const data = await response.data;
        dispatch(receiveAgreements(data));
        dispatch(completeTodoAction());
      } catch (error) {
        if ((error as any).response && (error as any).response.status === 401) {
          dispatch(setSignInDialogMessage('Invalid username/password'));
          return;
        }
        dispatch(setSignInDialogMessage('Unable to connect to Lasefiche'));
        return;
      }
      break;

    case 'fetchDocuments':
      const agreementIds = getDocumentsRequested(getState());
      const nextAgreementId = agreementIds[0];
      const documentsAPI = () => {
        if (!process.env) {
          return '';
        }
        if (process.env.NODE_ENV === 'production') {
          return `${process.env.REACT_APP_DOCUMENTS_API}${nextAgreementId}`;
        }
        if (process.env.NODE_ENV === 'development') {
          return process.env.REACT_APP_DOCUMENTS_API || '';
        }
        return '';
      };
      dispatch(setDocumentsLoading({ isLoading: true, agreementId: nextAgreementId }));
      try {
        const response = await axios.get(documentsAPI(), {
          headers: {
            Authorization: `Basic ${base64credentials}`
          }
        });
        const data = await response.data;
        const newDocuments = data.fetchedDocuments;
        dispatch(
          receiveDocuments({
            agreementId: nextAgreementId,
            documents: newDocuments
          })
        );
      } catch (error) {
        if ((error as any).response && (error as any).response.status === 401) {
          dispatch(setSignInDialogStatus(true));
          dispatch(setSignInDialogMessage('Invalid username/password'));
          return;
        }
        if ((error as any).response && (error as any).response.status === 404) {
          // TODO dynamically display errorMessages on IdentifyAgreements once TCTR-2 is merged
          dispatch(
            errorFetchingDocuments({
              agreementId: nextAgreementId,
              errorMessage: 'Unable to Load Files'
            })
          );
          return;
        }
        dispatch(
          errorFetchingDocuments({
            agreementId: nextAgreementId,
            errorMessage: 'Unable to connect to Lasefiche'
          })
        );
        return;
      }
      break;
  }

  if (getTodoActions(getState()).length) {
    dispatch(runAdditionalTodoActions());
  }

  dispatch(setSignInDialogStatus(false));
};
